<template>
    <div style="max-width: 600px; margin: auto; text-align: left;">
        <FieldHeader class="q-mb-xs" v-if="showHeader" :field="field" />
        <div
            v-for="(row, rowIndex) in renderedRows"
            :key="`cells-row-${rowIndex}`"
            class="row q-col-gutter-sm q-mb-sm cells-field-row"
        >
            <div
                v-for="cellField in row"
                :key="cellField.key"
                class="col-12 cells-field-cell"
                :style="columnStyle(cellField)"
            >
                <q-input
                    filled
                    dense
                    :type="inputType(cellField)"
                    :autogrow="isMultilineField(cellField)"
                    :model-value="getFieldValue(cellField.key)"
                    :readonly="readOnly"
                    :label="cellField.label"
                    :placeholder="inputPlaceholder(cellField)"
                    :stack-label="isDateField(cellField)"
                    :rules="inputRules(cellField)"
                    hide-bottom-space
                    @update:model-value="onUpdate(cellField.key, $event)"
                />
            </div>
        </div>
    </div>
</template>

<script>
    import FieldHeader from './FieldHeader.vue';

    export default {
        name: 'CellsField',
        components: { FieldHeader },
        props: {
            field: {
                type: Object,
                required: true,
            },
            modelValue: {
                type: [Object, String],
                default: () => ({}),
            },
            readOnly: {
                type: Boolean,
                default: false,
            },
            showHeader: {
                type: Boolean,
                default: true,
            },
        },
        emits: ['update:modelValue', 'blur'],
        computed: {
            // Retrieve field names from options.fields or options.values, with a fallback to a default field if neither is provided
            cellFields() {
                const fields = this.field?.options?.fields;
                if (Array.isArray(fields) && fields.length) return fields;

                const values = this.field?.options?.values;
                if (Array.isArray(values) && values.length) return values;

                return ['Response'];
            },
            // Use the split-columns numbers to group fields into rows, ensuring that fields with the same split-columns value are grouped together, and that fields without split-columns or with split-columns of 1 are placed in their own rows
            renderedRows() {
                const rows = [];
                let currentRow = [];
                let currentSplit = null;

                const flushCurrentRow = () => {
                    if (currentRow.length) {
                        rows.push(currentRow);
                        currentRow = [];
                        currentSplit = null;
                    }
                };

                this.normalizedCellFields.forEach((fieldDefinition) => {
                    if (!fieldDefinition.splitColumns || fieldDefinition.splitColumns <= 1) {
                        flushCurrentRow();
                        rows.push([fieldDefinition]);
                        return;
                    }

                    if (currentSplit !== fieldDefinition.splitColumns) {
                        flushCurrentRow();
                        currentSplit = fieldDefinition.splitColumns;
                    }

                    currentRow.push(fieldDefinition);

                    if (currentRow.length >= fieldDefinition.splitColumns) {
                        flushCurrentRow();
                    }
                });

                flushCurrentRow();

                return rows;
            },
            // Fallback for ensuring modelValue is always a defined object
            normalizedModelValue() {
                if (this.modelValue && typeof this.modelValue === 'object' && !Array.isArray(this.modelValue)) {
                    return this.modelValue;
                }
                return {};
            },
            normalizedCellFields() {
                return this.cellFields.map((fieldDef, index) => this.normalizeCellField(fieldDef, index));
            },
        },
        methods: {
            isDateField(cellField) {
                return this.inputType(cellField) === 'date';
            },
            isMultilineField(cellField) {
                return this.inputType(cellField) === 'textarea';
            },
            inputPlaceholder(cellField) {
                if (this.isDateField(cellField)) return '';
                return `Enter ${cellField.label.toLowerCase()}...`;
            },
            inputType(cellField) {
                return cellField?.type === 'text' ? 'textarea' : cellField?.type;
            },
            getFieldValue(cellField) {
                const value = this.normalizedModelValue[cellField];
                return typeof value === 'string' ? value : '';
            },
            // Capitalises and adds spaces to field keys for use as default labels (e.g. "first_name" becomes "First Name")
            toLabel(value) {
                return String(value || '')
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (char) => char.toUpperCase());
            },
            // Normalise field cells with defaults/fallback keys
            normalizeCellField(fieldDef, index) {
                if (typeof fieldDef === 'string') {
                    return {
                        key: fieldDef,
                        label: this.toLabel(fieldDef),
                        splitColumns: 1,
                        type: 'text',
                    };
                }

                if (fieldDef && typeof fieldDef === 'object' && !Array.isArray(fieldDef)) {
                    const [key, config] = Object.entries(fieldDef)[0] || [`field_${index + 1}`, {}];
                    const normalizedConfig = config && typeof config === 'object' ? config : {};
                    const inputType = this.normalizeInputType(normalizedConfig.type);
                    const splitColumns = this.normalizeSplitColumns(normalizedConfig.split);

                    return {
                        key,
                        label: this.toLabel(normalizedConfig.label || key),
                        splitColumns,
                        type: inputType,
                    };
                }

                const fallbackKey = `field_${index + 1}`;
                return {
                    key: fallbackKey,
                    label: this.toLabel(fallbackKey),
                    splitColumns: 1,
                    type: 'text',
                };
            },
            normalizeSplitColumns(splitValue) {
                if (splitValue === true) return 2;
                const numericValue = Number(splitValue);
                if (Number.isInteger(numericValue) && numericValue > 1 && numericValue <= 12) {
                    return numericValue;
                }
                return 1;
            },
            // Calculate column style using splitColumns value, or full-width default
            columnStyle(cellField) {
                if (!cellField?.splitColumns || cellField.splitColumns <= 1) {
                    return { width: '100%' };
                }

                const columnWidth = `${100 / cellField.splitColumns}%`;
                return {
                    width: columnWidth,
                    maxWidth: columnWidth,
                    flex: `0 0 ${columnWidth}`,
                };
            },
            // Cast input type against known set, with default fallback of 'text'
            normalizeInputType(type) {
                const normalized = String(type || 'text').toLowerCase();
                const supportedTypes = ['text', 'email', 'tel', 'number', 'url', 'password', 'search', 'date'];
                return supportedTypes.includes(normalized) ? normalized : 'text';
            },
            // Generate validation rules based on input type (e.g. email and phone number validation), with no rules for unsupported types
            inputRules(cellField) {
                if (cellField.type === 'email') {
                    return [(value) => this.validateOptionalEmail(value) || 'Please enter a valid email address'];
                }
                if (cellField.type === 'tel') {
                    return [(value) => this.validateOptionalPhone(value) || 'Please enter a valid phone number'];
                }
                return [];
            },
            validateOptionalEmail(value) {
                const normalized = String(value ?? '').trim();
                if (!normalized) return true;
                return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
            },
            validateOptionalPhone(value) {
                const normalized = String(value ?? '').trim();
                if (!normalized) return true;
                return /^[+()\-\s\d]{7,20}$/.test(normalized);
            },
            onUpdate(cellField, value) {
                const nextValue = {
                    ...this.normalizedModelValue,
                    [cellField]: value,
                };

                const hasAnyValue = this.normalizedCellFields.some(({ key }) => {
                    const current = nextValue[key];
                    return String(current ?? '').trim() !== '';
                });

                this.$emit('update:modelValue', hasAnyValue ? nextValue : '');
                this.$emit('blur');
            },
        },
    };
</script>

<style scoped>
    .cells-field-row {
        align-items: stretch;
    }

    .cells-field-cell {
        display: flex;
    }

    .cells-field-cell :deep(.q-field) {
        width: 100%;
        height: 100%;
    }

    .cells-field-cell :deep(.q-field__control) {
        height: 100%;
    }

    .cells-field-cell :deep(textarea.q-field__native) {
        height: 100%;
        min-height: 100%;
    }
</style>